import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useToggleRuleEnabledMutation,
  useToggleRulesEnabledMutation,
} from "store/queries/adjustmentRules";
import {
  useDeleteChargeBasedPricingMutation,
  useGetAllChargeBasedPricingQuery,
} from "store/queries/chargeBasedPricing";
import {
  useDeleteContractHierarchyRuleMutation,
  useGetAllContractHierarchyRulesQuery,
} from "store/queries/contractHierarchyRules";
import {
  useDeleteExplodingFeeScheduleMutation,
  useGetAllExplodingFeeSchedulesQuery,
} from "store/queries/explodingFeeSchedule";
import {
  useDeleteGlobalSurgicalPeriodRulesetMutation,
  useGetAllGlobalSurgicalPeriodRulesetsQuery,
} from "store/queries/globalSurgicalPeriodRulesets";
import {
  useDeleteInpatientRulesetMutation,
  useGetInpatientRulesetsQuery,
} from "store/queries/inpatientRulesets";
import {
  useDeleteInstitutionalRulesetMutation,
  useGetAllInstitutionalRulesetsQuery,
} from "store/queries/institutionalRuleset";
import {
  useDeleteLesserOfChargesMutation,
  useGetAllLesserOfChargesQuery,
} from "store/queries/lesserOfCharges";
import {
  useDeleteModifierAdjustmentMutation,
  useGetAllModifierAdjustmentsQuery,
} from "store/queries/modifierAdjustments";
import {
  useDeletePackageRulesetMutation,
  useGetAllPackageRulesetsQuery,
} from "store/queries/packageRuleset";
import {
  useDeleteCaseRateRulesetMutation,
  useGetCaseRateRulesetsQuery,
} from "store/queries/pricing";
import {
  useDeleteDiscountRulesetMutation,
  useGetAllDiscountRulesetsQuery,
} from "store/queries/procedureDiscountRuleset";
import {
  useDeleteProfessionalRulesetMutation,
  useGetAllProfessionalRulesetsQuery,
} from "store/queries/professionalRuleset";
import {
  useDeleteUnitCapMutation,
  useGetAllUnitCapQuery,
} from "store/queries/unitCap";
import styled from "styled-components";
import type { IRuleToggleType } from "types/AdjustmentRules/AdjustmentRules";
import type {
  IRulesetListParams,
  RulesetApplicability,
} from "types/DefaultRules/DefaultRules";
import type {
  IInpatientOutlierConfig,
  IPassThroughConfig,
  ITransferConfig,
  InpatientRulesetType,
} from "types/InpatientRuleset";

import { hydrateInpatientOutlierFormData } from "../../PayerRulesPage/helpers/inpatientOutlier";
import { ALL_PAYERS_LABEL } from "../../PayerRulesPage/helpers/ruleApplicability";
import {
  extractServerErrorMessage,
  payerCountLabel,
} from "../../PayerRulesPage/helpers/ruleBucket";
import {
  GROUPABLE_RULE_TYPES,
  groupRuleRows,
  isRuleBucketRow,
} from "../utils/groupRuleRows";
import { defaultScopeCell } from "./defaultScopeCell";
import { PayerChipsCell } from "./PayerChipsCell";

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledDataTable = styled(DataTable)`
  table > thead > tr > th {
    padding: 1rem 0.5rem !important;
  }
  table > tbody > tr > td {
    padding: 1rem 0.5rem !important;
  }
  /* CLE-2547 — a disabled rule stays listed and editable, but reads as inactive at a glance.
     The status switch itself is excluded so it keeps full contrast and stays obviously clickable. */
  table > tbody > tr.payer-rule-row--disabled > td:not(:nth-child(2)) {
    opacity: 0.45;
  }
`;

const ColumnWrapper = styled.div`
  display: block;
  max-height: 20px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const AdditionalInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const RuleTypeCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

/**
 * CLE-2547 — display label → the rule-type key the shared enable/disable endpoint expects.
 *
 * "Hierarchy" is deliberately absent: a hierarchy row in this table is a *group* of rules for one
 * provider/payer, so a single switch would be the wrong granularity. Those are toggled
 * individually on the hierarchy ruleset page, which already had an active/inactive flag.
 */
const TOGGLE_TYPE_BY_LABEL: Record<string, IRuleToggleType> = {
  MPPR: "MPPR",
  "Case Rate": "CASE_RATE",
  Package: "PACKAGE",
  "Exploding Fee Schedule": "EXPLODING_FEE",
  Inpatient: "INPATIENT",
  Professional: "PROFESSIONAL",
  Institutional: "INSTITUTIONAL",
  "Professional Modifiers": "PROFESSIONAL_MODIFIERS",
  "Global Surgical Period": "GLOBAL_SURGICAL_PERIOD",
  "Lesser-Of Charges": "LESSER_OF_CHARGES",
  "Charge-Based Pricing": "CHARGE_BASED_PRICING",
  "Unit Cap": "UNIT_CAP",
};

const RULE_TYPE_TAG_PALETTE: Record<
  string,
  { $bg: string; $fg: string; $border: string }
> = {
  MPPR: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  "Case Rate": {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
  Package: {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-200)",
  },
  "Exploding Fee Schedule": {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-300)",
  },
  Hierarchy: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  Inpatient: {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  Professional: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
  Institutional: {
    $bg: "var(--teal-50)",
    $fg: "var(--teal-700)",
    $border: "var(--teal-200)",
  },
  "Professional Modifiers": {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-300)",
  },
  "Global Surgical Period": {
    $bg: "var(--teal-50)",
    $fg: "var(--teal-700)",
    $border: "var(--teal-200)",
  },
  "Lesser-Of Charges": {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-200)",
  },
  "Charge-Based Pricing": {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
  "Unit Cap": {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
};

const StyledRuleTypeTag = styled(Tag)<{
  $bg: string;
  $fg: string;
  $border: string;
}>`
  &.p-tag.p-component {
    background: ${(p) => p.$bg} !important;
    color: ${(p) => p.$fg} !important;
    border: 1px solid ${(p) => p.$border} !important;
    font-weight: 600;
    font-size: 0.75rem;
    width: fit-content;
    border-radius: 8px;
  }
`;

const INPATIENT_TYPE_LABELS: Record<string, string> = {
  FIXED_COST: "Fixed Cost",
  PASS_THROUGH: "Pass Through",
  EXCESSIVE_LOS: "Excessive LOS",
  TRANSFER: "Transfer",
  STOP_LOSS: "Stop Loss",
  CHARGE_OUTLIER: "Charge Outlier",
  INPATIENT_OUTLIER: "Inpatient Outlier Rules",
};

/** Labels aligned with `InpatientRulesetForm` IPO trigger options (+ legacy engine types). */
const INPATIENT_OUTLIER_TRIGGER_LABELS: Record<string, string> = {
  STANDARD_CHARGE_THRESHOLD: "Standard Charge Threshold",
  MODIFIED_CHARGE_THRESHOLD: "Modified Charge Threshold (cost-to-charge)",
  LOS_RANGE: "LOS Range",
  PER_CODE_CHARGE_THRESHOLD: "Per Code Charge Threshold",
  DISCHARGE_STATUS: "Discharge Status",
  ALWAYS: "Always",
};

// CLE-2397 — professional MPDR primary-selection method labels for the Additional Info column.
const PRIMARY_SELECTION_METHOD_LABELS: Record<string, string> = {
  ENDO_FAMILY_RVU: "Endo family RVU",
  RVU: "RVU",
  CONTRACTED_DOLLARS: "Contracted dollars",
  BILATERAL_RVU_ADJUSTED: "Bilateral-adjusted RVU",
};

const TRANSFER_LOS_FORMULA_LABELS: Record<
  ITransferConfig["losFormula"],
  string
> = {
  LOS_MINUS_1: "Per Diem × (LOS - 1)",
  LOS: "Per Diem × LOS",
  LOS_PLUS_1: "Per Diem × (LOS + 1)",
};

function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function isInpatientOutlierConfig(
  config: unknown
): config is IInpatientOutlierConfig {
  return (
    typeof config === "object" &&
    config !== null &&
    "trigger" in config &&
    "formula" in config &&
    "mode" in config
  );
}

type SelectedRuleset = {
  id: string;
  ruleType:
    | "MPPR"
    | "Case Rate"
    | "Package"
    | "Exploding Fee Schedule"
    | "Hierarchy"
    | "Inpatient"
    | "Professional"
    | "Institutional"
    | "Professional Modifiers"
    | "Global Surgical Period"
    | "Lesser-Of Charges"
    | "Charge-Based Pricing"
    | "Unit Cap";
  providerId?: string;
  payerId?: number;
  payerName?: string;
  rules?: { id: string }[];
  // CLE-2842 — set on a bucket row (one rule copied to several payers); every member is acted on.
  memberIds?: string[];
  payerNames?: string[];
};

/** CLE-2842 — a bucket row stands for several rule rows; a plain row for itself. */
const rowIdsOf = (item: SelectedRuleset): string[] =>
  item.memberIds?.length ? item.memberIds : [item.id];

interface IPayerRulesTableProps {
  // CLE-2524 — "live" shows the rules the pricing engine actually applies
  // (payer-provider + fallback); "typical" shows inert templates that only take
  // effect once copied into a contract.
  mode: "live" | "typical";
}

type TScopeFilter = "BOTH" | "SCOPED" | "FALLBACK";

const SCOPE_FILTER_OPTIONS: { label: string; value: TScopeFilter }[] = [
  { label: "Payer-Provider & Fallback rules", value: "BOTH" },
  { label: "Payer-Provider rules", value: "SCOPED" },
  { label: "Fallback rules", value: "FALLBACK" },
];

// CLE-2842 step 2 — the six additive types (MPPR, Case Rate, Package, Exploding Fee Schedule,
// Inpatient, Unit Cap) now take the ladder types' shapes: a Fallback rule is every provider, and a
// payer-less rule (Fallback, or a Payer-Provider rule left on All payers) is every payer. The
// Typical tab hides both columns, so a template's values never show.
const additiveScopeNames = (ruleset: {
  applicability?: RulesetApplicability;
  provider?: { name?: string | null } | null;
  payer?: { name?: string | null } | null;
}) => ({
  providerName:
    ruleset.applicability === "FALLBACK"
      ? "All providers"
      : ruleset.provider?.name ?? "—",
  payerName: ruleset.payer?.name ?? ALL_PAYERS_LABEL,
});

function PayerRulesTable({ mode }: IPayerRulesTableProps) {
  const [selectedItems, setSelectedItems] = useState<SelectedRuleset[]>([]);
  const typical = mode === "typical";
  const [scopeFilter, setScopeFilter] = useState<TScopeFilter>("BOTH");
  const listParams = useMemo<IRulesetListParams>(() => {
    if (typical) return { applicability: ["TYPICAL"] };
    const applicability: RulesetApplicability[] =
      scopeFilter === "BOTH" ? ["SCOPED", "FALLBACK"] : [scopeFilter];
    return { applicability };
  }, [typical, scopeFilter]);
  // Hierarchy rules have no applicability concept — inherently payer-provider scoped, so they
  // only belong on the Live tab and drop out when the user filters to fallback rules only.
  const includeScopedOnlyTypes = !typical && scopeFilter !== "FALLBACK";
  // CLE-2597 — Professional/Institutional DO have applicability now (SCOPED | FALLBACK, never
  // TYPICAL). Their list endpoints return every row, so the scope filter is applied here.
  const matchesScopeFilter = (applicability?: string) => {
    if (typical) return false;
    const value = applicability ?? "SCOPED";
    if (scopeFilter === "BOTH") return true;
    return value === scopeFilter;
  };
  const { setIsLoading } = useLoading();
  const [deleteDiscountRuleset] = useDeleteDiscountRulesetMutation();
  const [deleteCaseRateRuleset] = useDeleteCaseRateRulesetMutation();
  const [deletePackageRuleset] = useDeletePackageRulesetMutation();
  const [deleteExplodingFeeSchedule] = useDeleteExplodingFeeScheduleMutation();
  const [deleteContractHierarchyRule] =
    useDeleteContractHierarchyRuleMutation();
  const [deleteInpatientRuleset] = useDeleteInpatientRulesetMutation();
  const [deleteProfessionalRuleset] = useDeleteProfessionalRulesetMutation();
  const [deleteInstitutionalRuleset] = useDeleteInstitutionalRulesetMutation();
  const [deleteModifierAdjustment] = useDeleteModifierAdjustmentMutation();
  const [deleteGlobalSurgicalPeriod] =
    useDeleteGlobalSurgicalPeriodRulesetMutation();
  const [deleteLesserOfCharges] = useDeleteLesserOfChargesMutation();
  const [deleteChargeBasedPricing] = useDeleteChargeBasedPricingMutation();
  const [deleteUnitCap] = useDeleteUnitCapMutation();

  const {
    data: discountRulesets,
    isLoading,
    refetch,
  } = useGetAllDiscountRulesetsQuery(listParams);
  const {
    data: caseRateRulesets,
    isLoading: isCaseRateLoading,
    refetch: refetchCaseRateRulesets,
  } = useGetCaseRateRulesetsQuery(listParams);
  const {
    data: packageRulesets,
    isLoading: isPackageLoading,
    refetch: refetchPackageRulesets,
  } = useGetAllPackageRulesetsQuery(listParams);
  const {
    data: explodingFeeSchedules,
    isLoading: isExplodingFeeSchedulesLoading,
    refetch: refetchExplodingFeeSchedules,
  } = useGetAllExplodingFeeSchedulesQuery(listParams);
  const {
    data: hierarchyRules,
    isLoading: isHierarchyRulesLoading,
    refetch: refetchHierarchyRules,
  } = useGetAllContractHierarchyRulesQuery();
  const {
    data: inpatientRulesets,
    isLoading: isInpatientRulesetsLoading,
    refetch: refetchInpatientRulesets,
  } = useGetInpatientRulesetsQuery(listParams);
  const {
    data: professionalRulesets,
    isLoading: isProfessionalRulesetsLoading,
    refetch: refetchProfessionalRulesets,
  } = useGetAllProfessionalRulesetsQuery();
  const {
    data: institutionalRulesets,
    isLoading: isInstitutionalRulesetsLoading,
    refetch: refetchInstitutionalRulesets,
  } = useGetAllInstitutionalRulesetsQuery();
  const {
    data: modifierAdjustments,
    isLoading: isModifierAdjustmentsLoading,
    refetch: refetchModifierAdjustments,
  } = useGetAllModifierAdjustmentsQuery(listParams);
  const {
    data: globalSurgicalPeriodRulesets,
    isLoading: isGlobalSurgicalPeriodLoading,
    refetch: refetchGlobalSurgicalPeriod,
  } = useGetAllGlobalSurgicalPeriodRulesetsQuery(listParams);
  const {
    data: lesserOfCharges,
    isLoading: isLesserOfChargesLoading,
    refetch: refetchLesserOfCharges,
  } = useGetAllLesserOfChargesQuery(listParams);
  const {
    data: chargeBasedPricing,
    isLoading: isChargeBasedPricingLoading,
    refetch: refetchChargeBasedPricing,
  } = useGetAllChargeBasedPricingQuery(listParams);
  const {
    data: unitCapRulesets,
    isLoading: isUnitCapLoading,
    refetch: refetchUnitCap,
  } = useGetAllUnitCapQuery(listParams);

  const { current: toastElement } = useContext(ToastContext);
  const navigate = useNavigate();
  const [toggleRuleEnabled] = useToggleRuleEnabledMutation();
  const [toggleRulesEnabled] = useToggleRulesEnabledMutation();
  // Rules whose toggle request is still in flight, so a row can't be double-clicked into an
  // ambiguous state while the list refetches.
  const [togglingIds, setTogglingIds] = useState<string[]>([]);

  async function handleToggleEnabled(row: any, enabled: boolean) {
    const ruleToggleType = TOGGLE_TYPE_BY_LABEL[row.ruleType];
    if (!ruleToggleType) return;

    setTogglingIds((ids) => [...ids, row.id]);
    try {
      await toggleRuleEnabled({
        ruleType: ruleToggleType,
        ruleId: row.id,
        enabled,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: enabled ? "Rule enabled" : "Rule disabled",
        detail: enabled
          ? `"${row.ruleName}" will apply to new estimates and claims.`
          : `"${row.ruleName}" will no longer apply to estimates or claims.`,
      });
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: `Could not ${enabled ? "enable" : "disable"} this rule.`,
      });
    } finally {
      setTogglingIds((ids) => ids.filter((id) => id !== row.id));
    }
  }

  // CLE-2842 — a bucket row's switch toggles every member in ONE atomic request: all of them
  // change or none do. Enabled state is part of the bucket key, so the members never disagree.
  async function handleToggleBucketEnabled(row: any, enabled: boolean) {
    const ruleToggleType = TOGGLE_TYPE_BY_LABEL[row.ruleType];
    if (!ruleToggleType) return;
    const memberIds: string[] = row.memberIds ?? [];

    setTogglingIds((ids) => [...ids, ...memberIds]);
    try {
      await toggleRulesEnabled({
        ruleType: ruleToggleType,
        ruleIds: memberIds,
        enabled,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: `Rule ${
          enabled ? "enabled" : "disabled"
        } for ${payerCountLabel(memberIds.length)}`,
        detail: enabled
          ? `"${row.ruleName}" will apply to new estimates and claims.`
          : `"${row.ruleName}" will no longer apply to estimates or claims.`,
      });
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          extractServerErrorMessage(error) ??
          `Could not ${enabled ? "enable" : "disable"} this rule.`,
      });
    } finally {
      setTogglingIds((ids) => ids.filter((id) => !memberIds.includes(id)));
    }
  }

  const parsedDiscountRulesets = useMemo(() => {
    if (!discountRulesets?.data) return [];
    return discountRulesets.data.map((aDiscountRuleset) => {
      return {
        ...aDiscountRuleset,
        ruleType: "MPPR",
        ruleName: aDiscountRuleset.groupName ?? "-",
        ...additiveScopeNames(aDiscountRuleset),
        allowOnDuplicateCptCodes: aDiscountRuleset.allowOnDuplicateCptCodes
          ? "Enabled"
          : "Disabled",
      };
    });
  }, [discountRulesets]);

  const parsedCaseRateRulesets = useMemo(() => {
    if (!caseRateRulesets?.data) return [];
    return caseRateRulesets.data.map((aCaseRateRuleset) => {
      return {
        ...aCaseRateRuleset,
        ruleType: "Case Rate",
        ruleName: aCaseRateRuleset.groupName ?? "-",
        ...additiveScopeNames(aCaseRateRuleset),
        allowOnDuplicateCptCodes: "—",
      };
    });
  }, [caseRateRulesets]);

  const parsedPackageRulesets = useMemo(() => {
    if (!packageRulesets?.data) return [];
    return packageRulesets.data.map((aPackageRuleset) => {
      return {
        ...aPackageRuleset,
        ruleType: "Package",
        ruleName: aPackageRuleset.groupName ?? "-",
        ...additiveScopeNames(aPackageRuleset),
        cptCodes: aPackageRuleset.triggerCptCodes ?? [],
        allowOnDuplicateCptCodes: "—",
      };
    });
  }, [packageRulesets]);

  const parsedExplodingFeeSchedules = useMemo(() => {
    if (!explodingFeeSchedules?.data) return [];
    return explodingFeeSchedules.data.map((aRuleset) => {
      const totalRate = aRuleset.totalRateInCents / 100;
      const cptCount = aRuleset.cptCodes?.length ?? 0;
      return {
        ...aRuleset,
        ruleType: "Exploding Fee Schedule",
        ruleName: aRuleset.groupName ?? "-",
        ...additiveScopeNames(aRuleset),
        allowOnDuplicateCptCodes: "—",
        totalRateFormatted: `$${totalRate.toFixed(2)}`,
        perCodeRateFormatted: `$${(cptCount > 0
          ? totalRate / cptCount
          : 0
        ).toFixed(2)}`,
      };
    });
  }, [explodingFeeSchedules]);

  const parsedHierarchyRules = useMemo(() => {
    if (!hierarchyRules?.data) return [];

    const groups = new Map<string, any[]>();

    hierarchyRules.data.forEach((rule) => {
      const key = `${rule.providerId}__${rule.payerId}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)?.push(rule);
    });

    return Array.from(groups.entries()).map(([key, rules]) => {
      const first = rules[0];
      return {
        id: key,
        key,
        ruleType: "Hierarchy" as const,
        providerId: first.providerId,
        payerId: first.payerId,
        providerName: first.provider?.name ?? "—",
        payerName: first.payer?.name ?? "—",
        ruleName: rules.map((r) => r.name).join(", "),
        groupName: rules.map((r) => r.name).join(", "),
        rules,
        ruleCount: rules.length,
        ruleSummary: rules.map((r) => r.name).join(", "),
        cptCodes: [],
        allowOnDuplicateCptCodes: "—",
      };
    });
  }, [hierarchyRules]);

  const parsedInpatientRulesets = useMemo(() => {
    if (!inpatientRulesets?.data) return [];
    return inpatientRulesets.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Inpatient" as const,
      ruleName: ruleset.name ?? "-",
      ...additiveScopeNames(ruleset),
      inpatientTypeLabel: INPATIENT_TYPE_LABELS[ruleset.type] || ruleset.type,
      cptCodes: ruleset.drgCodes ?? [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [inpatientRulesets]);

  const parsedProfessionalRulesets = useMemo(() => {
    if (!professionalRulesets?.data) return [];
    return professionalRulesets.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Professional" as const,
      ruleName: "—",
      // CLE-2597 — these two are now fallback-capable: a fallback has no provider, and either a
      // real payer or none. Naming that honestly is the point — it is what makes the system-wide
      // rule findable here instead of living invisibly in the engine.
      providerName:
        ruleset.applicability === "FALLBACK"
          ? "All providers"
          : ruleset.provider?.name ?? "—",
      payerName:
        ruleset.payer?.name ??
        (ruleset.applicability === "FALLBACK" ? "All payers" : "—"),
      cptCodes: [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [professionalRulesets]);

  const parsedInstitutionalRulesets = useMemo(() => {
    if (!institutionalRulesets?.data) return [];
    return institutionalRulesets.data.map((ruleset: any) => ({
      ...ruleset,
      ruleType: "Institutional" as const,
      ruleName: "—",
      // CLE-2597 — these two are now fallback-capable: a fallback has no provider, and either a
      // real payer or none. Naming that honestly is the point — it is what makes the system-wide
      // rule findable here instead of living invisibly in the engine.
      providerName:
        ruleset.applicability === "FALLBACK"
          ? "All providers"
          : ruleset.provider?.name ?? "—",
      payerName:
        ruleset.payer?.name ??
        (ruleset.applicability === "FALLBACK" ? "All payers" : "—"),
      cptCodes: [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [institutionalRulesets]);

  const parsedModifierAdjustments = useMemo(() => {
    if (!modifierAdjustments?.data) return [];
    return modifierAdjustments.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Professional Modifiers" as const,
      ruleName: ruleset.groupName ?? `Modifier ${ruleset.modifier}`,
      // CLE-2524 — fallback rows are live for every provider/payer without a more
      // specific rule; typical rows are inert templates. A scoped rule with no payer
      // applies to every payer for its provider.
      providerName:
        ruleset.applicability === "SCOPED"
          ? ruleset.provider?.name ?? "—"
          : "All providers",
      // CLE-2597 — a FALLBACK row may name a payer, and the whole point is that it is
      // findable by that payer here. Only a payer-less fallback reads "All payers".
      payerName: ruleset.payer?.name ?? "All payers",
      cptCodes: ruleset.cptCodes ?? [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [modifierAdjustments]);

  // CLE-2836 — the global surgical period. No code list of its own: the CPTs it prices
  // to $0 are the denied lines it finds on the claim, so the CPT column stays empty and
  // the Additional Info column carries the three terms instead.
  const parsedGlobalSurgicalPeriod = useMemo(() => {
    if (!globalSurgicalPeriodRulesets?.data) return [];
    return globalSurgicalPeriodRulesets.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Global Surgical Period" as const,
      ruleName: ruleset.groupName ?? "Global Surgical Period",
      providerName:
        ruleset.applicability === "SCOPED"
          ? ruleset.provider?.name ?? "—"
          : "All providers",
      // CLE-2597 — a FALLBACK row may name a payer, and the whole point is that it is
      // findable by that payer here. Only a payer-less fallback reads "All payers".
      payerName: ruleset.payer?.name ?? "All payers",
      cptCodes: [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [globalSurgicalPeriodRulesets]);

  const parsedLesserOfCharges = useMemo(() => {
    if (!lesserOfCharges?.data) return [];
    return lesserOfCharges.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Lesser-Of Charges" as const,
      ruleName:
        ruleset.groupName ??
        (ruleset.scope === "LINE_CHARGES"
          ? "Lesser of (line)"
          : "Lesser of (total)"),
      providerName:
        ruleset.applicability === "SCOPED"
          ? ruleset.provider?.name ?? "—"
          : "All providers",
      // CLE-2597 — a FALLBACK row may name a payer, and the whole point is that it is
      // findable by that payer here. Only a payer-less fallback reads "All payers".
      payerName: ruleset.payer?.name ?? "All payers",
      // Backend unified cptCodes + drgCodes into a single `codes` column; render that
      // here under the shared "CPT/DRG Codes" column the table already has.
      cptCodes: ruleset.codes ?? [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [lesserOfCharges]);

  const parsedChargeBasedPricing = useMemo(() => {
    if (!chargeBasedPricing?.data) return [];
    return chargeBasedPricing.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Charge-Based Pricing" as const,
      // Wildcard charge-based rules (no codes) are the common case ("price every
      // claim at multiplier × charges"), so the auto-name reflects that when
      // the user didn't enter a description.
      ruleName:
        ruleset.groupName ??
        (ruleset.scope === "LINE_CHARGES"
          ? "Charge-Based (line)"
          : "Charge-Based (total)"),
      providerName:
        ruleset.applicability === "SCOPED"
          ? ruleset.provider?.name ?? "—"
          : "All providers",
      // CLE-2597 — a FALLBACK row may name a payer, and the whole point is that it is
      // findable by that payer here. Only a payer-less fallback reads "All payers".
      payerName: ruleset.payer?.name ?? "All payers",
      cptCodes: ruleset.codes ?? [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [chargeBasedPricing]);

  const parsedUnitCap = useMemo(() => {
    if (!unitCapRulesets?.data) return [];
    return unitCapRulesets.data.map((ruleset) => ({
      ...ruleset,
      ruleType: "Unit Cap" as const,
      ruleName:
        ruleset.groupName ??
        (ruleset.maxUnitsPerCode === 1
          ? "Once per service"
          : `Max ${ruleset.maxUnitsPerCode}/service`),
      ...additiveScopeNames(ruleset),
      cptCodes: ruleset.cptCodes ?? [],
      allowOnDuplicateCptCodes: "—",
    }));
  }, [unitCapRulesets]);

  const parsedRulesets = useMemo(() => {
    const combined = [
      ...parsedDiscountRulesets,
      ...parsedCaseRateRulesets,
      ...parsedPackageRulesets,
      ...parsedExplodingFeeSchedules,
      // Hierarchy / Professional / Institutional have no applicability concept —
      // inherently payer-provider rules, so they belong on the Live tab only.
      ...(includeScopedOnlyTypes ? parsedHierarchyRules : []),
      ...parsedInpatientRulesets,
      // CLE-2597 — Professional/Institutional are fallback-capable now, so they are filtered by
      // applicability like the ladder types rather than dropped from the Fallback view wholesale.
      ...parsedProfessionalRulesets.filter((r: any) =>
        matchesScopeFilter(r.applicability)
      ),
      ...parsedInstitutionalRulesets.filter((r: any) =>
        matchesScopeFilter(r.applicability)
      ),
      // The remaining types are filtered by applicability in their list queries.
      ...parsedModifierAdjustments,
      ...parsedGlobalSurgicalPeriod,
      ...parsedLesserOfCharges,
      ...parsedChargeBasedPricing,
      ...parsedUnitCap,
    ];
    const withSearchText = combined.map((row) => ({
      ...row,
      cptCodesSearchText: Array.isArray(row.cptCodes)
        ? row.cptCodes.join(", ")
        : "",
    }));
    // CLE-2842 — one row per rule bucket (same rule, same provider, several payers). Typical
    // templates have no groupKey, but the tab is left out explicitly so it can never change.
    return typical ? withSearchText : groupRuleRows(withSearchText as any[]);
  }, [
    typical,
    includeScopedOnlyTypes,
    parsedDiscountRulesets,
    parsedCaseRateRulesets,
    parsedPackageRulesets,
    parsedExplodingFeeSchedules,
    parsedHierarchyRules,
    parsedInpatientRulesets,
    parsedProfessionalRulesets,
    parsedInstitutionalRulesets,
    parsedModifierAdjustments,
    parsedGlobalSurgicalPeriod,
    parsedLesserOfCharges,
    parsedChargeBasedPricing,
    parsedUnitCap,
  ]);

  function ruleStatusToggle(row: any) {
    // Hierarchy rows are a group, not a single rule — see TOGGLE_TYPE_BY_LABEL.
    if (!TOGGLE_TYPE_BY_LABEL[row.ruleType]) return null;
    const enabled = row.disabledAt == null;

    return (
      <Row>
        <InputSwitch
          checked={enabled}
          disabled={togglingIds.includes(row.id)}
          tooltip={
            enabled
              ? "Rule is live — applies to estimates and claims"
              : "Rule is disabled — applies to neither estimates nor claims"
          }
          onChange={(event) =>
            isRuleBucketRow(row) && row.members.length > 1
              ? handleToggleBucketEnabled(row, !!event.value)
              : handleToggleEnabled(row, !!event.value)
          }
        />
      </Row>
    );
  }

  function payerRuleAction(
    row: {
      id: string;
      ruleType: string;
      providerId?: string;
      payerId?: number;
    },
    action: "copy" | "edit" | "viewEdit"
  ) {
    const routePrefixByType: Record<string, string> = {
      "Case Rate": "case-rate",
      Package: "packages",
      "Exploding Fee Schedule": "exploding-fee-schedules",
      Inpatient: "inpatient-rulesets",
      MPPR: "mppr",
      Professional: "professional-rulesets",
      Institutional: "institutional-rulesets",
      "Professional Modifiers": "professional-modifiers",
      "Global Surgical Period": "global-surgical-period",
      "Lesser-Of Charges": "lesser-of-charges",
      "Charge-Based Pricing": "charge-based-pricing",
      "Unit Cap": "unit-cap",
    };
    const routePrefix = routePrefixByType[row.ruleType];
    const hierarchyEditPath = `/payer-rules/hierarchy-rules/ruleset/${row.providerId}/${row.payerId}`;
    const actionPath = action === "copy" ? "copy" : "edit";
    const targetPath =
      row.ruleType === "Hierarchy"
        ? hierarchyEditPath
        : `/payer-rules/${routePrefix ?? "mppr"}/${actionPath}/${row.id}`;
    const actionLabel = action === "copy" ? "Copy" : "View/Edit";

    return (
      <Row>
        <Button className="p-button-link" onClick={() => navigate(targetPath)}>
          <p className="small-text" style={{ whiteSpace: "nowrap" }}>
            {actionLabel}
          </p>
        </Button>
      </Row>
    );
  }

  // CLE-2842 — a bucket row lists its payers as chips; a single rule reads exactly as before.
  const payerTemplate = (row: any) =>
    isRuleBucketRow(row) && row.members.length > 1 ? (
      <PayerChipsCell rowId={row.id} payerNames={row.payerNames} />
    ) : (
      defaultScopeCell(row, row.payerName)
    );

  const cptCodesTransformer = (cptCodes: string[]) => {
    const allCodes = cptCodes.join(", ");
    return <ColumnWrapper>{allCodes}</ColumnWrapper>;
  };

  const ruleTypeTemplate = (row: {
    ruleType: string;
    inpatientTypeLabel?: string;
  }) => {
    const palette = RULE_TYPE_TAG_PALETTE[row.ruleType] ?? {
      $bg: "var(--primary-50)",
      $fg: "var(--primary-800)",
      $border: "var(--primary-300)",
    };
    return (
      <RuleTypeCell>
        <StyledRuleTypeTag
          value={row.ruleType}
          $bg={palette.$bg}
          $fg={palette.$fg}
          $border={palette.$border}
        />
        {row.ruleType === "Inpatient" && row.inpatientTypeLabel ? (
          <span className="text-sm text-gray-500">
            {row.inpatientTypeLabel}
          </span>
        ) : null}
      </RuleTypeCell>
    );
  };

  const additionalInfoTemplate = (row: {
    ruleType: string;
    type?: InpatientRulesetType;
    config?: unknown;
    discountPercentForRank2?: number;
    discountPercentForRank3?: number;
    discountPercentForRank4?: number;
    discountPercentForRank5Plus?: number;
    isPerDiem?: boolean;
    isBilateral?: boolean;
    bundledCptCodes?: string[];
    totalRateFormatted?: string;
    perCodeRateFormatted?: string;
    ruleCount?: number;
    ruleSummary?: string;
    allowOnDuplicateCptCodes?: string;
    compoundBilateralAndMppr?: boolean;
    endoApplies?: boolean;
    primarySelectionMethod?: string;
    modifier?: string;
    multiplier?: number;
    scope?: string;
    maxAmountInCents?: number | null;
    maxUnitsPerCode?: number;
    lookbackDays?: number;
    requireGlobalRemark?: boolean;
  }) => {
    // CLE-2836 — the two terms, in the words the form uses.
    if (row.ruleType === "Global Surgical Period") {
      return (
        <AdditionalInfoWrapper>
          <span>{`Lookback: ${row.lookbackDays ?? 90} days`}</span>
          <span>
            {row.requireGlobalRemark
              ? "Trigger: N525 / M144 only"
              : "Trigger: remark or clean CO-97"}
          </span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Professional Modifiers") {
      const m = Number(row.multiplier ?? 1);
      const multiplierLabel = `×${m.toFixed(4).replace(/\.?0+$/, "")}`;
      return (
        <AdditionalInfoWrapper>
          <span>{`Modifier: ${row.modifier ?? "—"}`}</span>
          <span>{`Multiplier: ${multiplierLabel}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Lesser-Of Charges") {
      const m = Number(row.multiplier ?? 1);
      const multiplierLabel = `×${m.toFixed(4).replace(/\.?0+$/, "")}`;
      const scopeLabel =
        row.scope === "TOTAL_CHARGES" ? "Total charges" : "Line charges";
      return (
        <AdditionalInfoWrapper>
          <span>{`Scope: ${scopeLabel}`}</span>
          <span>{`Multiplier: ${multiplierLabel}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Charge-Based Pricing") {
      const m = Number(row.multiplier ?? 1);
      const multiplierLabel = `×${m.toFixed(4).replace(/\.?0+$/, "")}`;
      const scopeLabel =
        row.scope === "TOTAL_CHARGES" ? "Total charges" : "Line charges";
      const maxAmountLabel =
        row.maxAmountInCents !== null && row.maxAmountInCents !== undefined
          ? (row.maxAmountInCents / 100).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            })
          : null;
      return (
        <AdditionalInfoWrapper>
          <span>{`Scope: ${scopeLabel}`}</span>
          <span>{`Multiplier: ${multiplierLabel}`}</span>
          {maxAmountLabel && <span>{`Max: ${maxAmountLabel}`}</span>}
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Unit Cap") {
      const cap = Number(row.maxUnitsPerCode ?? 1);
      return (
        <AdditionalInfoWrapper>
          <span>{`Max units/code: ${cap}`}</span>
          <span className="text-sm text-gray-600">
            {cap === 1 ? "Once per service" : "Per service"}
          </span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "MPPR") {
      return (
        <AdditionalInfoWrapper>
          <span>{`${row.discountPercentForRank2 ?? 0}% | ${
            row.discountPercentForRank3 ?? 0
          }% | ${row.discountPercentForRank4 ?? 0}% | ${
            row.discountPercentForRank5Plus ?? 0
          }%`}</span>
          <span className="text-sm text-gray-600">{`Duplicates: ${
            row.allowOnDuplicateCptCodes === "Enabled" ? "ON" : "OFF"
          }`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Case Rate") {
      return (
        <AdditionalInfoWrapper>
          <span>{`Per Diem: ${row.isPerDiem ? "Yes" : "No"}`}</span>
          <span>{`Bilateral: ${row.isBilateral ? "Yes" : "No"}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Package") {
      const bundledCodes = row.bundledCptCodes?.join(", ");
      const bundledText = `Bundled: ${bundledCodes || "-"}`;
      return <ColumnWrapper title={bundledText}>{bundledText}</ColumnWrapper>;
    }

    if (row.ruleType === "Exploding Fee Schedule") {
      return (
        <AdditionalInfoWrapper>
          <span>{`Total Rate: ${row.totalRateFormatted ?? "$0.00"}`}</span>
          <span>{`Per-Code Rate: ${row.perCodeRateFormatted ?? "$0.00"}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Hierarchy") {
      return (
        <AdditionalInfoWrapper>
          <span>{`Rules Count: ${row.ruleCount ?? 0}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Institutional") {
      const primaryMethodLabel =
        PRIMARY_SELECTION_METHOD_LABELS[
          row.primarySelectionMethod ?? "CONTRACTED_DOLLARS"
        ] ?? "Contracted dollars";
      return (
        <AdditionalInfoWrapper>
          <span>{`Primary: ${primaryMethodLabel}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Professional") {
      const primaryMethodLabel =
        PRIMARY_SELECTION_METHOD_LABELS[
          row.primarySelectionMethod ?? "ENDO_FAMILY_RVU"
        ] ?? "Endo family RVU";
      return (
        <AdditionalInfoWrapper>
          <span>{`Primary: ${primaryMethodLabel}`}</span>
          <span>{`Compound Bilateral + MPPR: ${
            row.compoundBilateralAndMppr ? "ON" : "OFF"
          }`}</span>
          <span>{`Endoscopy Rules: ${row.endoApplies ? "ON" : "OFF"}`}</span>
        </AdditionalInfoWrapper>
      );
    }

    if (row.ruleType === "Inpatient" && row.type && row.config != null) {
      if (
        row.type === "INPATIENT_OUTLIER" &&
        isInpatientOutlierConfig(row.config)
      ) {
        const hydrated = hydrateInpatientOutlierFormData(row.config);
        const triggerKey = hydrated.ipoTriggerType;
        const triggerLabel =
          (triggerKey && INPATIENT_OUTLIER_TRIGGER_LABELS[triggerKey]) ||
          triggerKey ||
          "—";
        return (
          <AdditionalInfoWrapper>
            <span>{`Trigger type: ${triggerLabel}`}</span>
          </AdditionalInfoWrapper>
        );
      }

      if (row.type === "PASS_THROUGH") {
        const cfg = row.config as IPassThroughConfig;
        const threshold =
          typeof cfg.thresholdInCents === "number"
            ? formatUsdFromCents(cfg.thresholdInCents)
            : "—";
        const mult =
          typeof cfg.chargeMultiplier === "number"
            ? String(cfg.chargeMultiplier)
            : "—";
        return (
          <AdditionalInfoWrapper>
            <span>{`Charge Threshold: ${threshold}`}</span>
            <span>{`Charge Multiplier: ${mult}`}</span>
          </AdditionalInfoWrapper>
        );
      }

      if (row.type === "TRANSFER") {
        const cfg = row.config as ITransferConfig;
        const losLabel =
          (cfg.losFormula && TRANSFER_LOS_FORMULA_LABELS[cfg.losFormula]) ||
          cfg.losFormula ||
          "—";
        return (
          <AdditionalInfoWrapper>
            <span>{`LOS Formula: ${losLabel}`}</span>
          </AdditionalInfoWrapper>
        );
      }
    }

    return "—";
  };

  const handleDeletePayerRules = async () => {
    setIsLoading(true);
    try {
      const selectedMpprIds = selectedItems
        .filter((item) => item.ruleType === "MPPR")
        .flatMap(rowIdsOf);
      const selectedCaseRateIds = selectedItems
        .filter((item) => item.ruleType === "Case Rate")
        .flatMap(rowIdsOf);
      const selectedPackageIds = selectedItems
        .filter((item) => item.ruleType === "Package")
        .flatMap(rowIdsOf);
      const selectedExplodingFeeScheduleIds = selectedItems
        .filter((item) => item.ruleType === "Exploding Fee Schedule")
        .map((item) => item.id);
      const selectedHierarchyRuleIds = selectedItems
        .filter((item) => item.ruleType === "Hierarchy")
        .flatMap((item) => item.rules?.map((rule) => rule.id) ?? []);
      const selectedInpatientIds = selectedItems
        .filter((item) => item.ruleType === "Inpatient")
        .map((item) => item.id);
      const selectedProfessionalIds = selectedItems
        .filter((item) => item.ruleType === "Professional")
        .map((item) => item.id);
      const selectedInstitutionalIds = selectedItems
        .filter((item) => item.ruleType === "Institutional")
        .map((item) => item.id);
      const selectedModifierAdjustmentIds = selectedItems
        .filter((item) => item.ruleType === "Professional Modifiers")
        .map((item) => item.id);
      const selectedGlobalSurgicalPeriodIds = selectedItems
        .filter((item) => item.ruleType === "Global Surgical Period")
        .map((item) => item.id);
      const selectedChargeBasedPricingIds = selectedItems
        .filter((item) => item.ruleType === "Charge-Based Pricing")
        .map((item) => item.id);
      const selectedUnitCapIds = selectedItems
        .filter((item) => item.ruleType === "Unit Cap")
        .map((item) => item.id);
      const selectedLesserOfChargesIds = selectedItems
        .filter((item) => item.ruleType === "Lesser-Of Charges")
        .map((item) => item.id);

      await selectedMpprIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteDiscountRuleset(id).unwrap()),
        Promise.resolve()
      );

      await selectedCaseRateIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteCaseRateRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedPackageIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deletePackageRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedExplodingFeeScheduleIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteExplodingFeeSchedule(id).unwrap()),
        Promise.resolve()
      );
      await selectedHierarchyRuleIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteContractHierarchyRule(id).unwrap()),
        Promise.resolve()
      );
      await selectedInpatientIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteInpatientRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedProfessionalIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteProfessionalRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedInstitutionalIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteInstitutionalRuleset(id).unwrap()),
        Promise.resolve()
      );
      await selectedModifierAdjustmentIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteModifierAdjustment(id).unwrap()),
        Promise.resolve()
      );
      await selectedGlobalSurgicalPeriodIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteGlobalSurgicalPeriod(id).unwrap()),
        Promise.resolve()
      );
      await selectedLesserOfChargesIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteLesserOfCharges(id).unwrap()),
        Promise.resolve()
      );
      await selectedChargeBasedPricingIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteChargeBasedPricing(id).unwrap()),
        Promise.resolve()
      );
      await selectedUnitCapIds.reduce(
        (promiseChain, id) =>
          promiseChain.then(() => deleteUnitCap(id).unwrap()),
        Promise.resolve()
      );

      await refetch();
      await refetchCaseRateRulesets();
      await refetchPackageRulesets();
      await refetchExplodingFeeSchedules();
      await refetchHierarchyRules();
      await refetchInpatientRulesets();
      await refetchProfessionalRulesets();
      await refetchInstitutionalRulesets();
      await refetchModifierAdjustments();
      await refetchGlobalSurgicalPeriod();
      await refetchLesserOfCharges();
      await refetchChargeBasedPricing();
      await refetchUnitCap();
      toastElement?.show({
        severity: "success",
        summary: "Deleted!",
        detail: "Payer Rules(s) has been deleted successfully.",
      });
    } catch (error) {
      await refetch();
      await refetchCaseRateRulesets();
      await refetchPackageRulesets();
      await refetchExplodingFeeSchedules();
      await refetchHierarchyRules();
      await refetchInpatientRulesets();
      await refetchProfessionalRulesets();
      await refetchInstitutionalRulesets();
      await refetchModifierAdjustments();
      await refetchGlobalSurgicalPeriod();
      await refetchLesserOfCharges();
      await refetchChargeBasedPricing();
      await refetchUnitCap();
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Error while deleting payer rules",
      });
    } finally {
      setIsLoading(false);
      setSelectedItems([]);
    }
  };

  // CLE-2842 — a selected bucket row deletes one rule row per payer, so say how many.
  const deleteConfirmationMessage = () => {
    const base = "Are you sure you want delete selected payer rules?";
    const hasBucket = selectedItems.some(
      (item) =>
        GROUPABLE_RULE_TYPES.includes(item.ruleType) &&
        (item.memberIds?.length ?? 0) > 1
    );
    if (!hasBucket) return base;
    const rowCount = selectedItems.reduce(
      (sum, item) =>
        sum +
        (item.ruleType === "Hierarchy"
          ? item.rules?.length ?? 0
          : rowIdsOf(item).length),
      0
    );
    const payers = new Set(
      selectedItems
        .flatMap((item) => item.payerNames ?? [item.payerName])
        .filter(Boolean)
    );
    return `${base} This deletes ${rowCount} rule row${
      rowCount === 1 ? "" : "s"
    } across ${payerCountLabel(payers.size)}.`;
  };

  const ruleTypeFilterTemplate = (options: any) => {
    const filterOptions = [
      "MPPR",
      "Case Rate",
      "Package",
      "Exploding Fee Schedule",
      // Hierarchy / Professional / Institutional cannot be typical templates.
      ...(typical ? [] : ["Hierarchy", "Professional", "Institutional"]),
      "Inpatient",
      "Professional Modifiers",
      "Global Surgical Period",
      "Lesser-Of Charges",
      "Charge-Based Pricing",
      "Unit Cap",
    ];
    return (
      <Dropdown
        value={options.value}
        options={filterOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select One"
        className="p-column-filter"
        showClear
        style={{ minWidth: "11rem", maxWidth: "11rem" }}
      />
    );
  };

  const textFilterTemplate = (options: any) => (
    <InputText
      value={options.value ?? ""}
      onChange={(e) => options.filterApplyCallback(e.target.value)}
      placeholder="Search"
      className="p-column-filter"
      style={{ minWidth: "200px", maxWidth: "200px" }}
    />
  );

  if (
    isLoading ||
    isCaseRateLoading ||
    isPackageLoading ||
    isExplodingFeeSchedulesLoading ||
    isHierarchyRulesLoading ||
    isInpatientRulesetsLoading ||
    isProfessionalRulesetsLoading ||
    isInstitutionalRulesetsLoading ||
    isModifierAdjustmentsLoading ||
    isGlobalSurgicalPeriodLoading ||
    isLesserOfChargesLoading ||
    isChargeBasedPricingLoading ||
    isUnitCapLoading
  ) {
    return <LoadingSpinner />;
  }
  return (
    <>
      {typical ? (
        <ToolbarRow>
          <span style={{ fontSize: 13, color: "#6b7280" }}>
            Typical rules are templates — they have no effect until copied into
            a contract.
          </span>
        </ToolbarRow>
      ) : (
        <ToolbarRow>
          <span style={{ fontSize: 13 }}>Scope</span>
          <Dropdown
            value={scopeFilter}
            options={SCOPE_FILTER_OPTIONS}
            onChange={(e) => setScopeFilter(e.value)}
            style={{ minWidth: "16rem" }}
          />
        </ToolbarRow>
      )}
      <StyledDataTable
        rows={10}
        paginator
        stripedRows
        className="p-fluid"
        filterDisplay="row"
        value={parsedRulesets}
        responsiveLayout="scroll"
        style={{ cursor: "pointer" }}
        selection={selectedItems}
        emptyMessage="No payer rules to show."
        selectionMode="single"
        onSelectionChange={(event) => setSelectedItems(event.value)}
        rowClassName={(row: any) =>
          row.disabledAt != null ? "payer-rule-row--disabled" : ""
        }
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
        {/* An inert template can't be enabled or disabled — hide the switch on the Typical tab. */}
        {!typical && (
          <Column
            field="status"
            header="Status"
            style={{ minWidth: "110px", maxWidth: "110px" }}
            body={(row) => ruleStatusToggle(row)}
          />
        )}
        <Column
          filter
          field="ruleType"
          filterMatchMode="equals"
          style={{ minWidth: "140px" }}
          header="Rule Type"
          filterElement={ruleTypeFilterTemplate}
          showFilterMenu={false}
          body={ruleTypeTemplate}
        />
        {/* Typical templates have no provider/payer — the scope columns only exist on the Live tab. */}
        {!typical && (
          <Column
            filter
            field="providerName"
            filterMatchMode="contains"
            style={{ minWidth: "230px", maxWidth: "230px" }}
            header="Provider"
            filterElement={textFilterTemplate}
            showFilterMenu={false}
            body={(row) => defaultScopeCell(row, row.providerName)}
          />
        )}
        {!typical && (
          <Column
            filter
            field="payerName"
            filterMatchMode="contains"
            style={{ minWidth: "230px", maxWidth: "230px" }}
            header="Payer"
            filterElement={textFilterTemplate}
            showFilterMenu={false}
            body={payerTemplate}
          />
        )}
        <Column
          filter
          field="ruleName"
          filterMatchMode="contains"
          style={{ minWidth: "230px", maxWidth: "230px" }}
          header="Rule Name"
          filterElement={textFilterTemplate}
          showFilterMenu={false}
        />
        <Column
          filter
          field="cptCodesSearchText"
          filterMatchMode="contains"
          body={(row) => cptCodesTransformer(row.cptCodes)}
          style={{ minWidth: "230px", maxWidth: "230px" }}
          header="CPT/DRG Codes"
          filterElement={textFilterTemplate}
          showFilterMenu={false}
        />
        <Column
          field="additionalInfo"
          style={{ minWidth: "230px", maxWidth: "230px" }}
          header="Additional Info"
          body={additionalInfoTemplate}
        />
        <Column
          style={{ maxWidth: "100px" }}
          field="viewCopyPayerRule"
          body={(row) => {
            if (row.ruleType === "Hierarchy")
              return payerRuleAction(row, "viewEdit");
            // Professional rulesets are unique per (provider, payer) so copy doesn't apply.
            if (row.ruleType === "Professional") return null;
            // Same for institutional rulesets.
            if (row.ruleType === "Institutional") return null;
            return payerRuleAction(row, "copy");
          }}
        />
        <Column
          style={{ minWidth: "120px", maxWidth: "120px" }}
          field="viewEditPayerRule"
          body={(row) =>
            row.ruleType === "Hierarchy" ? null : payerRuleAction(row, "edit")
          }
        />
      </StyledDataTable>
      <div className="flex justify-content-end">
        <Button
          label="Delete"
          className="p-button-outlined mt-4"
          disabled={!selectedItems.length}
          onClick={() => {
            confirmDialog({
              header: "Confirmation",
              icon: "pi pi-exclamation-triangle",
              message: deleteConfirmationMessage(),
              accept: handleDeletePayerRules,
            });
          }}
        />
      </div>
    </>
  );
}

export default PayerRulesTable;
